import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router';
import { setTransport } from './api/client';
import { AppProvider, useApp } from './app-context';
import { Layout } from './components/Layout';
import { MaintenanceBanner } from './components/MaintenanceBanner';
import { Audit, Jobs } from './pages/Activity';
import { AcceptInvite, Login, Setup, ResetCredentials } from './pages/Auth';
import { CreateServer } from './pages/CreateServer';
import { Velocity } from './pages/Velocity';
import { Dashboard } from './pages/Dashboard';
import { OAuthConsent } from './pages/OAuthConsent';
import { Requests } from './pages/Requests';
import { Routing } from './pages/Routing';
import { Templates } from './pages/Templates';
import { ServerDetail } from './pages/ServerDetail';
import { Users } from './pages/Users';
import { Settings } from './pages/Settings';
import './styles.css';
import './mobile.css';
import './motion.css';

function RequireLogin() {
  const { me } = useApp();
  if (!me) return null;
  if (!me.user) {
    const next = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={me.setupRequired ? '/setup' : `/login?next=${next}`} replace />;
  }
  return <Layout />;
}

function App() {
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/reset/:token" element={<ResetCredentials />} />
      <Route path="/setup" element={<Setup />} />
      <Route path="/invite/:token" element={<AcceptInvite />} />
      <Route path="/oauth/consent" element={<OAuthConsent />} />
      <Route element={<RequireLogin />}>
        <Route index element={<Dashboard />} />
        <Route path="servers/new" element={<CreateServer />} />
        <Route path="velocity" element={<Velocity />} />
        <Route path="servers/:id" element={<ServerDetail />} />
        <Route path="requests" element={<Requests />} />
        <Route path="templates" element={<Templates />} />
        <Route path="routing" element={<Routing />} />
        <Route path="jobs" element={<Jobs />} />
        <Route path="audit" element={<Audit />} />
        <Route path="users" element={<Users />} />
        <Route path="settings" element={<Settings />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Route>
    </Routes>
  );
}

async function boot() {
  // npm run dev:mock（vite --mode mock）でバックエンド無しのモックを使う
  if (import.meta.env.MODE === 'mock' || import.meta.env.VITE_MOCK === '1') {
    const { mockTransport } = await import('./api/mock');
    setTransport(mockTransport);
  }
  const root = document.getElementById('root');
  if (!root) throw new Error('#root is missing');
  createRoot(root).render(
    <StrictMode>
      <BrowserRouter>
        <AppProvider>
          <MaintenanceBanner />
          <App />
        </AppProvider>
      </BrowserRouter>
    </StrictMode>,
  );
}

void boot();
